import { useState, useEffect, useRef } from 'react';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import {
  Box,
  Container,
  Grid,
  TextField,
  Button,
  Typography,
  Link,
  IconButton,
  InputAdornment,
  Paper,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions
} from '@mui/material';
import { Visibility, VisibilityOff, Refresh } from '@mui/icons-material';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { authAPI } from '../../services/api';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    companyName: '',
    email: '',
    password: '',
    confirmPassword: '',
    phoneNumber: '',
    category: '',
    states: ''
  });
  const [fieldErrors, setFieldErrors] = useState({});
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [otpDialogOpen, setOtpDialogOpen] = useState(false);
  const [otp, setOtp] = useState(['', '', '', '']);
  const [verifying, setVerifying] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [captchaInput, setCaptchaInput] = useState('');
  
  // Refs for OTP input fields
  const otpRefs = [useRef(null), useRef(null), useRef(null), useRef(null)];

  const categories = [
    'Corporate',
    'CA',
    'CMA',
    'CS',
    'Tax Practitioner',
    'Advocate',
    'Other'
  ];

  const indianStates = [
    'Andhra Pradesh',
    'Arunachal Pradesh',
    'Assam',
    'Bihar',
    'Chhattisgarh',
    'Goa',
    'Gujarat',
    'Haryana',
    'Himachal Pradesh',
    'Jharkhand',
    'Karnataka',
    'Kerala',
    'Madhya Pradesh',
    'Maharashtra',
    'Manipur',
    'Meghalaya',
    'Mizoram',
    'Nagaland',
    'Odisha',
    'Punjab',
    'Rajasthan',
    'Sikkim',
    'Tamil Nadu',
    'Telangana',
    'Tripura',
    'Uttar Pradesh',
    'Uttarakhand',
    'West Bengal',
    'Andaman and Nicobar Islands',
    'Chandigarh',
    'Dadra and Nagar Haveli and Daman and Diu',
    'Delhi',
    'Jammu and Kashmir',
    'Ladakh',
    'Lakshadweep',
    'Puducherry'
  ];

  // Generate random captcha
  const generateCaptcha = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let result = '';
    for (let i = 0; i < 6; i++) {
      result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setCaptcha(result);
    setCaptchaInput('');
  };

  // Generate captcha on component mount
  useEffect(() => {
    generateCaptcha();
  }, []);

  const validatePassword = (password) => {
    const errors = [];
    
    if (password.length < 6 || password.length > 14) {
      errors.push('Password must be 6-14 characters long');
    }
    if (!/[A-Z]/.test(password)) {
      errors.push('Password must contain at least 1 capital letter');
    }
    if (!/[0-9]/.test(password)) {
      errors.push('Password must contain at least 1 number');
    }
    if (!/[!@#$%^&*(),.?":{}|<>]/.test(password)) {
      errors.push('Password must contain at least 1 special character');
    }
    
    return errors;
  };

  const validateField = (name, value) => {
    const errors = {};
    
    switch(name) {
      case 'firstName':
      case 'lastName':
        if (!value.trim()) {
          errors[name] = 'This field is required';
        } else if (value.trim().length < 2) {
          errors[name] = 'Must be at least 2 characters';
        } else if (!/^[a-zA-Z\s]+$/.test(value)) {
          errors[name] = 'Only letters are allowed';
        }
        break;
        
      case 'companyName':
        if (!value.trim()) {
          errors[name] = 'Company name is required';
        } else if (value.trim().length < 2) {
          errors[name] = 'Must be at least 2 characters';
        }
        break;
        
      case 'email':
        if (!value.trim()) {
          errors[name] = 'Email is required';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
          errors[name] = 'Invalid email format';
        }
        break;
        
      case 'phoneNumber':
        if (!value.trim()) {
          errors[name] = 'Phone number is required';
        } else if (!/^\d{10}$/.test(value)) {
          errors[name] = 'Phone number must be exactly 10 digits';
        }
        break;
        
      case 'category':
        if (!value) {
          errors[name] = 'Please select a category';
        }
        break;
        
      case 'states':
        if (!value) {
          errors[name] = 'Please select a state';
        }
        break;
        
      default:
        break;
    }
    
    return errors;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // For phone number, only allow digits and limit to 10
    if (name === 'phoneNumber') {
      const digitsOnly = value.replace(/\D/g, '').slice(0, 10);
      setFormData({
        ...formData,
        [name]: digitsOnly
      });
      
      // Validate on change
      const errors = validateField(name, digitsOnly);
      setFieldErrors(prev => ({
        ...prev,
        [name]: errors[name] || ''
      }));
    } else {
      setFormData({
        ...formData,
        [name]: value
      });
      
      // Validate on change
      const errors = validateField(name, value);
      setFieldErrors(prev => ({
        ...prev,
        [name]: errors[name] || ''
      }));
    }
    
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    // Validate all fields
    let allErrors = {};
    Object.keys(formData).forEach(key => {
      if (key !== 'confirmPassword') {
        const errors = validateField(key, formData[key]);
        allErrors = { ...allErrors, ...errors };
      }
    });

    // Check if there are any validation errors
    if (Object.keys(allErrors).length > 0) {
      setFieldErrors(allErrors);
      Object.values(allErrors).forEach(error => toast.error(error));
      setLoading(false);
      return;
    }

    // Validate password
    const passwordErrors = validatePassword(formData.password);
    if (passwordErrors.length > 0) {
      passwordErrors.forEach(error => toast.error(error));
      setLoading(false);
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      toast.error('Passwords do not match');
      setLoading(false);
      return;
    }

    if (captchaInput !== captcha) {
      toast.error('Invalid captcha code. Please try again.');
      generateCaptcha();
      setLoading(false);
      return;
    }

    try {
      // Send OTP to email
      const response = await authAPI.sendOTP({
        email: formData.email,
        firstName: formData.firstName,
        lastName: formData.lastName
      });
      
      if (response.success) {
        setOtpDialogOpen(true);
        toast.success('OTP sent to your email!');
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to send OTP. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleOTPChange = (index, value) => {
    // Only allow single digit
    if (value.length > 1) {
      value = value.slice(-1);
    }
    
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newOtp = [...otp];
    newOtp[index] = value;
    setOtp(newOtp);

    // Auto-focus next input
    if (value && index < 3) {
      otpRefs[index + 1].current?.focus();
    }
  };

  const handleOTPKeyDown = (index, e) => {
    // Handle backspace
    if (e.key === 'Backspace' && !otp[index] && index > 0) {
      otpRefs[index - 1].current?.focus();
    } else if (e.key === 'Enter' && otp.join('').length === 4) {
      handleVerifyOTP();
    }
  };

  const handleVerifyOTP = async () => {
    const otpString = otp.join('');
    
    if (otpString.length !== 4) {
      toast.error('Please enter a valid 4-digit OTP');
      return;
    }

    setVerifying(true);

    try {
      const { confirmPassword, ...registrationData } = formData;
      const response = await authAPI.verifyOTP({
        ...registrationData,
        otp: otpString
      });
      
      if (response.success) {
        toast.success('Registration successful! Redirecting to login...');
        setTimeout(() => {
          navigate('/');
        }, 1500);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'Invalid OTP. Please try again.');
    } finally {
      setVerifying(false);
    }
  };

  const handleCloseOTPDialog = () => {
    setOtpDialogOpen(false);
    setOtp(['', '', '', '']);
  };

  const handleGoToLogin = () => {
    navigate('/');
  };

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center', 
      justifyContent: 'center', 
      bgcolor: '#f5f5f5', 
      py: { xs: 2, sm: 4 },
      px: { xs: 2, sm: 0 }
    }}>
      <ToastContainer
        position="top-right"
        autoClose={3000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
      <Container maxWidth="md">
        <Paper elevation={3} sx={{ 
          p: { xs: 3, sm: 4 }, 
          borderRadius: 2 
        }}>
          <Typography 
            variant="h4" 
            align="center" 
            gutterBottom 
            sx={{ 
              fontWeight: 600, 
              mb: { xs: 3, sm: 4 },
              fontSize: { xs: '1.75rem', sm: '2.125rem' }
            }}
          >
            Registration
          </Typography>

          <Box component="form" onSubmit={handleSubmit} autoComplete="off">
            <Grid container spacing={{ xs: 2, sm: 3 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="First Name"
                  name="firstName"
                  value={formData.firstName}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  size="small"
                  error={!!fieldErrors.firstName}
                  helperText={fieldErrors.firstName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  value={formData.lastName}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  size="small"
                  error={!!fieldErrors.lastName}
                  helperText={fieldErrors.lastName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Company Name"
                  name="companyName"
                  value={formData.companyName}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  size="small"
                  error={!!fieldErrors.companyName}
                  helperText={fieldErrors.companyName}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Email Address"
                  type="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  size="small"
                  error={!!fieldErrors.email}
                  helperText={fieldErrors.email}
                />
              </Grid>
              <Grid item xs={12}>
                <Box>
                  <TextField
                    fullWidth
                    label="Password"
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    required
                    autoComplete="new-password"
                    size="small"
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            onClick={() => setShowPassword(!showPassword)}
                            edge="end"
                            size="small"
                          >
                            {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                  <Box
                    sx={{
                      mt: 0.5,
                      p: 1,
                      bgcolor: '#f5f5f5',
                      borderRadius: 1,
                      border: '1px solid #e0e0e0',
                    }}
                  >
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{
                        fontSize: { xs: '0.688rem', sm: '0.75rem' },
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: { xs: 0.5, sm: 1 },
                      }}
                    >
                      <span>• 6-14 characters</span>
                      <span>• 1 uppercase</span>
                      <span>• 1 number</span>
                      <span>• 1 special char</span>
                    </Typography>
                  </Box>
                </Box>
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type={showConfirmPassword ? 'text' : 'password'}
                  name="confirmPassword"
                  value={formData.confirmPassword}
                  onChange={handleChange}
                  required
                  autoComplete="new-password"
                  size="small"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          edge="end"
                          size="small"
                        >
                          {showConfirmPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Phone Number"
                  name="phoneNumber"
                  value={formData.phoneNumber}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  size="small"
                  error={!!fieldErrors.phoneNumber}
                  helperText={fieldErrors.phoneNumber || "Enter 10 digit phone number"}
                  inputProps={{
                    maxLength: 10,
                    inputMode: 'numeric',
                    pattern: '[0-9]*'
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="Category"
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  size="small"
                  error={!!fieldErrors.category}
                  helperText={fieldErrors.category}
                >
                  <MenuItem value="">Select</MenuItem>
                  {categories.map((option) => (
                    <MenuItem key={option} value={option}>
                      {option}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  select
                  label="States"
                  name="states"
                  value={formData.states}
                  onChange={handleChange}
                  required
                  autoComplete="off"
                  size="small"
                  error={!!fieldErrors.states}
                  helperText={fieldErrors.states}
                >
                  <MenuItem value="">Select</MenuItem>
                  {indianStates.map((state) => (
                    <MenuItem key={state} value={state}>
                      {state}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    label="Captcha Code"
                    value={captchaInput}
                    onChange={(e) => setCaptchaInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        handleSubmit(e);
                      }
                    }}
                    required
                    autoComplete="off"
                    size="small"
                    placeholder="Enter code"
                  />
                  <Box
                    sx={{
                      minWidth: { xs: 120, sm: 140 },
                      height: 40,
                      bgcolor: '#f5f5f5',
                      border: '1px solid #ddd',
                      borderRadius: 1,
                      px: { xs: 1, sm: 2 },
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      position: 'relative',
                      flexShrink: 0
                    }}
                  >
                    <Typography
                      sx={{
                        fontFamily: 'monospace',
                        fontSize: { xs: '16px', sm: '20px' },
                        fontWeight: 600,
                        letterSpacing: '2px',
                        color: '#1976d2',
                        textDecoration: 'line-through',
                        textDecorationColor: '#666',
                        userSelect: 'none'
                      }}
                    >
                      {captcha}
                    </Typography>
                    <IconButton
                      onClick={generateCaptcha}
                      size="small"
                      sx={{ 
                        position: 'absolute',
                        right: 2,
                        top: '50%',
                        transform: 'translateY(-50%)',
                        padding: '4px'
                      }}
                    >
                      <Refresh fontSize="small" />
                    </IconButton>
                  </Box>
                </Box>
              </Grid>
            </Grid>

            {error && (
              <Typography color="error" variant="body2" sx={{ mt: 2, textAlign: 'center' }}>
                {error}
              </Typography>
            )}

            <Box sx={{ 
              mt: { xs: 3, sm: 4 }, 
              display: 'flex', 
              flexDirection: 'column', 
              alignItems: 'center', 
              gap: 2 
            }}>
              <Button
                type="submit"
                variant="contained"
                disabled={loading}
                sx={{ 
                  px: { xs: 4, sm: 6 }, 
                  py: 1,
                  width: { xs: '100%', sm: 'auto' }
                }}
              >
                {loading ? 'Sending OTP...' : 'Sign Up'}
              </Button>

              <Typography 
                variant="body2"
                sx={{ fontSize: { xs: '0.875rem', sm: '0.875rem' } }}
              >
                Already have an account?{' '}
                <Link component={RouterLink} to="/" sx={{ fontWeight: 600 }}>
                  Sign in instead
                </Link>
              </Typography>
            </Box>
          </Box>
        </Paper>
      </Container>

      {/* OTP Verification Dialog */}
      <Dialog 
        open={otpDialogOpen} 
        onClose={handleCloseOTPDialog} 
        maxWidth="xs" 
        fullWidth
        sx={{
          '& .MuiDialog-paper': {
            margin: { xs: 2, sm: 4 }
          }
        }}
      >
        <DialogTitle sx={{ 
          textAlign: 'center', 
          pb: 1, 
          fontWeight: 600, 
          color: '#1976d2', 
          fontSize: { xs: '1.25rem', sm: '1.5rem' }
        }}>
          Verify Your Email
        </DialogTitle>
        <DialogContent sx={{ px: { xs: 2, sm: 3 } }}>
          <Box sx={{ textAlign: 'center', mb: 3 }}>
            <Typography 
              variant="body2" 
              color="text.secondary"
              sx={{ fontSize: { xs: '0.813rem', sm: '0.875rem' } }}
            >
              We've sent a 4-digit OTP to
            </Typography>
            <Typography 
              variant="body1" 
              sx={{ 
                fontWeight: 600, 
                mt: 1,
                fontSize: { xs: '0.875rem', sm: '1rem' },
                wordBreak: 'break-word'
              }}
            >
              {formData.email}
            </Typography>
          </Box>

          <Box sx={{ 
            display: 'flex', 
            gap: { xs: 1, sm: 2 }, 
            justifyContent: 'center', 
            mb: 2 
          }}>
            {otp.map((digit, index) => (
              <TextField
                key={index}
                inputRef={otpRefs[index]}
                value={digit}
                onChange={(e) => handleOTPChange(index, e.target.value)}
                onKeyDown={(e) => handleOTPKeyDown(index, e)}
                inputProps={{
                  maxLength: 1,
                  style: { 
                    textAlign: 'center', 
                    fontSize: window.innerWidth < 600 ? '24px' : '32px',
                    fontWeight: 600,
                    padding: window.innerWidth < 600 ? '12px' : '16px'
                  }
                }}
                sx={{
                  width: { xs: '48px', sm: '64px' },
                  '& .MuiOutlinedInput-root': {
                    '& fieldset': {
                      borderWidth: 2,
                      borderColor: digit ? '#1976d2' : '#ddd'
                    }
                  }
                }}
                autoFocus={index === 0}
              />
            ))}
          </Box>

          <Typography 
            variant="caption" 
            color="text.secondary" 
            sx={{ 
              display: 'block', 
              mt: 2, 
              textAlign: 'center',
              fontSize: { xs: '0.688rem', sm: '0.75rem' }
            }}
          >
            OTP expires in 10 minutes
          </Typography>
        </DialogContent>
        <DialogActions sx={{ 
          flexDirection: 'column', 
          gap: 1, 
          p: { xs: 2, sm: 3 }, 
          pt: 0 
        }}>
          <Button
            fullWidth
            variant="contained"
            onClick={handleVerifyOTP}
            disabled={verifying || otp.some(digit => !digit)}
          >
            {verifying ? 'Verifying...' : 'Verify & Register'}
          </Button>
          <Button
            fullWidth
            variant="outlined"
            onClick={handleGoToLogin}
          >
            Go to Login
          </Button>
          <Button
            fullWidth
            variant="text"
            onClick={handleCloseOTPDialog}
            size="small"
          >
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Register;
